/**
 * The key under which `attribute` is actually present in `obj`, or `undefined`.
 *
 * Returns `attribute` itself when present -- the common case, no aliasing. When a
 * wire-true `nanosec` path is absent, it resolves against the decoded `nsec` key
 * instead: ROS2 `builtin_interfaces/Time` (e.g. `header.stamp`) and `Duration` are
 * recorded in a topic's schema with their wire subfield names (`sec`/`nanosec`),
 * but the web-ui decodes MCAP client-side with the Foxglove deserializers, which
 * normalize ROS2 time to `{ sec, nsec }` -- emitting `nsec`, never the wire name
 * `nanosec` (see `@foxglove/rosmsg2-serialization` for `ros2msg`, and
 * `@foxglove/ros2idl-parser`, which explicitly renames `nanosec` -> `nsec`). An
 * older path already recorded as `nsec` matches the decoded `nsec` directly, and
 * `sec` needs no alias -- both conventions spell it the same. Returns `undefined`
 * when neither the attribute nor its alias is present, so callers can treat that
 * as "not found"; a non-object `obj` yields `undefined` for the same reason.
 */
export function resolveMessagePathKey(
  obj: unknown,
  attribute: string,
): string | undefined {
  if (typeof obj !== "object" || obj === null) {
    return undefined;
  }
  if (attribute in obj) {
    return attribute;
  }
  if (attribute === "nanosec" && "nsec" in obj) {
    return "nsec";
  }
  return undefined;
}
